import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useSocket } from './useSocket';
import { useAutoOnlineStatus } from './useAutoOnlineStatus';
import { useAutoAvailability } from './useAutoAvailability';
import { usePeerConsultationNotifications, IncomingCall } from './usePeerConsultationNotifications';
import { useDashboardSync } from './useDashboardSync';
import { useOnlineUsers } from './useOnlineUsers';
import { logger } from '../utils/logger';
import messagingService from '../services/api-services/messagingService';

import { useNotifications } from './useNotifications';

interface UseDashboardLayoutReturn {
  // State
  sidebarOpen: boolean;
  avatarMenuOpen: boolean;
  unreadCount: number;
  unreadNotificationsCount: number;
  onlineUsersCount: number;
  isRealtimeConnected: boolean;
  incomingCall: IncomingCall | null;
  // Handlers
  toggleSidebar: () => void;
  closeSidebar: () => void;
  toggleAvatarMenu: () => void;
  closeAvatarMenu: () => void;
  acceptCall: () => void;
  declineCall: () => void;
  // Refs
  avatarMenuRef: React.RefObject<HTMLDivElement>;
}

/**
 * Custom hook managing all state and logic for DashboardLayout
 * Handles sidebar, avatar menu, notifications, and realtime connection status
 */
export const useDashboardLayout = (): UseDashboardLayoutReturn => {
  const { user, isAuthenticated, loading } = useAuth();
  const queryClient = useQueryClient();
  useAutoOnlineStatus();
  useAutoAvailability();

  const { isConnected: isSocketConnected } = useSocket();
  const avatarMenuRef = useRef<HTMLDivElement>(null);

  // Sidebar and Avatar menu state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);

  // Fetch unread notifications count
  const { unreadCount: unreadNotificationsCount } = useNotifications();

  // Fetch unread count
  const { data: unreadCount = 0 } = useQuery(
    ['unreadCount'],
    async () => {
      try {
        return await messagingService.getUnreadCount();
      } catch (error) {
        logger.error('Erreur récupération messages non lus', error);
        return 0;
      }
    },
    {
      enabled: isAuthenticated && !!user && !loading,
    }
  );

  // Online users
  const { onlineDoctors, onlinePatients } = useOnlineUsers();
  const onlineUsersCount = onlineDoctors.length + onlinePatients.length;

  // Dashboard sync with realtime connection
  const { incomingCall, acceptCall, declineCall } = usePeerConsultationNotifications();
  useDashboardSync({
    enableRealtime: true,
    onDataUpdate: () => {
      logger.dashboard('Données mises à jour (Invalidation unreadCount)');
      queryClient.invalidateQueries(['unreadCount']);
    },
  });

  const isRealtimeConnected = isSocketConnected;

  // Toggle handlers
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const toggleAvatarMenu = useCallback(() => {
    setAvatarMenuOpen((prev) => !prev);
  }, []);

  const closeAvatarMenu = useCallback(() => {
    setAvatarMenuOpen(false);
  }, []);

  // Click outside handler for avatar menu
  const handleClickOutside = useCallback((event: MouseEvent | TouchEvent) => {
    const target = event.target as Node;
    if (avatarMenuRef.current && !avatarMenuRef.current.contains(target)) {
      setAvatarMenuOpen(false);
    }
  }, []);

  // Escape key handler
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setAvatarMenuOpen(false);
      setSidebarOpen(false);
    }
  }, []);

  // Setup and cleanup event listeners
  useEffect(() => {
    if (avatarMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [avatarMenuOpen, handleClickOutside, handleEscapeKey]);

  return {
    sidebarOpen,
    avatarMenuOpen,
    unreadCount,
    unreadNotificationsCount,
    onlineUsersCount,
    isRealtimeConnected,
    incomingCall,
    toggleSidebar,
    closeSidebar,
    toggleAvatarMenu,
    closeAvatarMenu,
    acceptCall,
    declineCall,
    avatarMenuRef,
  };
};
